// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// The shared agent conversation -- the persona-labeled thread that Merlin, Athena
// (WS4) and later Socrates, Seldon (WS5) all write into (spec §2). Pure functional
// core: an immutable `Conversation` plus transitions that advance an agent turn
// working -> (message | proposal | error | cancelled). The UI (Activities-zone
// Agents tab) is the imperative shell over this; persistence is serialize/parse,
// so the thread is saved with the model and reopens intact (eval D1).
//
// Two invariants make the hard cases fall out:
//   - resolveTurn only advances a turn that is still `working`. If the user
//     cancelled (or the turn already resolved), a late-arriving result is a
//     no-op -- no half-applied edit, the clean-cancel guarantee (H2 / DC2).
//   - decideProposal only acts on a `pending` proposal, so a proposal can't be
//     accepted twice or accepted after being rejected (the HITL accept/reject).
//
// IDs are supplied by the caller (kept out of the pure core) so transitions stay
// deterministic and testable.

import type { JsonModel } from '@simlin/engine';

import type { AgentPersona } from './agent-personas';

/** What a proposal would change -- mirrors the server's diff, for preview + accept. */
export interface ProposalSummary {
  readonly proposalId: string;
  readonly summary: string;
  readonly added: readonly string[];
  readonly changed: readonly string[];
  readonly removed: readonly string[];
}

/**
 * The client-facing result of an agent turn. Mirrors the server MerlinOutcome
 * (server/merlin.ts) across the wire, minus the applied model the shell resolves
 * via the engine. `clarify` and `decline` both arrive as a `message` (they render
 * identically -- agent text, no edit); `unavailable` is the graceful failure path.
 */
export type AgentResult =
  | { readonly kind: 'proposal'; readonly proposal: ProposalSummary }
  | { readonly kind: 'message'; readonly text: string }
  | { readonly kind: 'unavailable'; readonly reason: string };

/**
 * A conversation result plus the model to apply if the user accepts. The Editor
 * feeds `result` into `resolveTurn`, and keeps `proposedModel` (present iff
 * `result.kind === 'proposal'`) to apply on accept, undoably.
 */
export interface ProposeResult {
  readonly result: AgentResult;
  readonly proposedModel?: JsonModel;
}

/**
 * A prior turn distilled to role + text, threaded to the agent as context so a
 * guided, loop-by-loop build carries what was asked and what Merlin already did
 * (AC4.3). Working/error/cancelled turns carry no content and are omitted.
 */
export interface ContextTurn {
  readonly role: 'user' | 'agent';
  readonly text: string;
}

/**
 * Propose a model edit from a plain-language prompt. Injected into the Editor as
 * a prop (like onSave) so the diagram package stays backend-agnostic: the app
 * supplies the real implementation (POST to the server), the demo a stub.
 * `history` carries the prior conversation context for loop-by-loop builds.
 */
export type ProposeFn = (input: {
  readonly prompt: string;
  readonly model: JsonModel;
  readonly modelName?: string;
  readonly history?: readonly ContextTurn[];
}) => Promise<ProposeResult>;

/**
 * Describe the current model in plain language (Athena the Scribe, WS4). A
 * read-only call -- it never changes the model, so it resolves to a `message`
 * turn (or `unavailable`), never a proposal. Reading the CURRENT model at call
 * time is what makes the Merlin -> Athena handoff read the post-edit state (H3).
 * Injected into the Editor like ProposeFn, keeping the diagram backend-agnostic.
 */
export type DescribeFn = (input: {
  readonly model: JsonModel;
  readonly modelName?: string;
  readonly history?: readonly ContextTurn[];
}) => Promise<AgentResult>;

export type ProposalDecision = 'pending' | 'accepted' | 'rejected';

export type AgentTurnState =
  | { readonly status: 'working' }
  | { readonly status: 'message'; readonly text: string }
  | { readonly status: 'proposal'; readonly proposal: ProposalSummary; readonly decision: ProposalDecision }
  | { readonly status: 'error'; readonly reason: string }
  | { readonly status: 'cancelled' };

export type Turn =
  | { readonly id: string; readonly role: 'user'; readonly text: string }
  | { readonly id: string; readonly role: 'agent'; readonly persona: AgentPersona; readonly state: AgentTurnState };

export interface Conversation {
  readonly turns: readonly Turn[];
}

export const EMPTY_CONVERSATION: Conversation = { turns: [] };

/** Find a turn by id (or undefined). */
export function findTurn(conv: Conversation, id: string): Turn | undefined {
  return conv.turns.find((t) => t.id === id);
}

/** The id of the most recent agent turn still `working`, if any (for cancel). */
export function latestWorkingTurnId(conv: Conversation): string | undefined {
  for (let i = conv.turns.length - 1; i >= 0; i--) {
    const t = conv.turns[i];
    if (t.role === 'agent' && t.state.status === 'working') {
      return t.id;
    }
  }
  return undefined;
}

/** Append a user message. */
export function appendUser(conv: Conversation, id: string, text: string): Conversation {
  return { turns: [...conv.turns, { id, role: 'user', text }] };
}

/** Append an agent turn in the `working` state (streaming / the ~60s path). */
export function appendWorking(conv: Conversation, id: string, persona: AgentPersona): Conversation {
  return { turns: [...conv.turns, { id, role: 'agent', persona, state: { status: 'working' } }] };
}

// Replace the state of an agent turn `id`, but only when the current state passes
// `guard`. A non-agent turn, a missing id, or a failing guard leaves the
// conversation unchanged (returns the same reference).
function mapAgentTurn(
  conv: Conversation,
  id: string,
  guard: (state: AgentTurnState) => boolean,
  next: (state: AgentTurnState) => AgentTurnState,
): Conversation {
  let changed = false;
  const turns = conv.turns.map((t) => {
    if (t.id !== id || t.role !== 'agent' || !guard(t.state)) {
      return t;
    }
    changed = true;
    return { ...t, state: next(t.state) };
  });
  return changed ? { turns } : conv;
}

function resultToState(result: AgentResult): AgentTurnState {
  switch (result.kind) {
    case 'proposal':
      return { status: 'proposal', proposal: result.proposal, decision: 'pending' };
    case 'message':
      return { status: 'message', text: result.text };
    case 'unavailable':
      return { status: 'error', reason: result.reason };
  }
}

/**
 * Advance a `working` agent turn to its result. A no-op if the turn is no longer
 * working (e.g. the user cancelled first) -- so a late result never overwrites a
 * clean cancel (H2 / DC2).
 */
export function resolveTurn(conv: Conversation, id: string, result: AgentResult): Conversation {
  return mapAgentTurn(conv, id, (s) => s.status === 'working', () => resultToState(result));
}

/** Cancel a `working` agent turn (stop mid-stream). A no-op if it already resolved. */
export function cancelTurn(conv: Conversation, id: string): Conversation {
  return mapAgentTurn(conv, id, (s) => s.status === 'working', () => ({ status: 'cancelled' }));
}

/**
 * Record the user's accept/reject on a `pending` proposal (HITL). A no-op if the
 * turn is not a pending proposal, so a proposal can't be decided twice.
 */
export function decideProposal(conv: Conversation, id: string, decision: 'accepted' | 'rejected'): Conversation {
  return mapAgentTurn(
    conv,
    id,
    (s) => s.status === 'proposal' && s.decision === 'pending',
    (s) => (s.status === 'proposal' ? { ...s, decision } : s),
  );
}

// ---- Persistence (D1: saved with the model, reopens intact) ----

interface SerializedConversation {
  readonly version: 1;
  readonly turns: readonly Turn[];
}

/** Serialize the thread for storage alongside the model. */
export function serializeConversation(conv: Conversation): string {
  const payload: SerializedConversation = { version: 1, turns: conv.turns };
  return JSON.stringify(payload);
}

/**
 * Parse a stored thread. Defensive like `parseTier`: never throws -- malformed or
 * unrecognized input yields an empty conversation rather than a crash on reopen.
 */
export function parseConversation(json: string): Conversation {
  try {
    const parsed = JSON.parse(json) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      Array.isArray((parsed as { turns?: unknown }).turns)
    ) {
      return { turns: (parsed as SerializedConversation).turns };
    }
  } catch {
    // fall through to empty
  }
  return EMPTY_CONVERSATION;
}

// ---- Context threading (AC4.3 loop-by-loop / H4 graceful degrade) ----

function agentTurnText(state: AgentTurnState): string | undefined {
  switch (state.status) {
    case 'message':
      return state.text;
    case 'proposal':
      return state.proposal.summary;
    default:
      // working / error / cancelled carry no useful context.
      return undefined;
  }
}

/**
 * Distil the conversation into an ordered context payload for the agent -- what
 * the user asked and what Merlin already proposed/did -- so a guided build can
 * proceed loop-by-loop with context carried through (AC4.3). Trimmed to the most
 * recent `maxTurns` so a long conversation degrades gracefully rather than
 * overflowing the model's context window (H4).
 */
export function conversationContext(conv: Conversation, maxTurns = 20): ContextTurn[] {
  const out: ContextTurn[] = [];
  for (const turn of conv.turns) {
    if (turn.role === 'user') {
      out.push({ role: 'user', text: turn.text });
    } else {
      const text = agentTurnText(turn.state);
      if (text !== undefined) {
        out.push({ role: 'agent', text });
      }
    }
  }
  return out.slice(-maxTurns);
}
